import { Menu, MenuItem } from "@mui/material";
import { MuiBox, MuiTypography } from "components/atoms";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "components/ui";
import { EllipsisVertical, Trash, Ban } from "lucide-react";

import {
  DeleteUserSessionsAction,
  InactiveUserSessionsAction,
} from "store/middlewares/user";
import { openAdminModal, closeAdminModal } from "store/slices/common/adminModal.slice";

const UserSessionActionBar = ({ row, userId, filterObj }) => {
  const dispatch = useDispatch();

  //--------------------------------------------------- Action Menu Array --------------------------------------
  const ActionMenuArray = [
    row?.is_active && {
      label: "Inactive",
      icon: <Ban size={16} color="#475569" />,
      onClick: () => {
        dispatch(
          openAdminModal({
            title: "",
            content: (
              <div className="grid gap-5 p-1">
                {/* Title with icon */}
                <div className="flex items-center gap-3.5">
                  <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                    <Ban className="size-5" aria-hidden="true" />
                  </div>
                  <h2 className="text-xl font-bold text-foreground">
                    Inactivate this session?
                  </h2>
                </div>

                {/* What happens */}
                <ul className="grid gap-2">
                  <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
                    <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
                    <span className="break-words">
                      This active session will be immediately logged out.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
                    <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
                    <span>The user will no longer be able to use this device session.</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
                    <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
                    <span>You can delete it permanently later if needed.</span>
                  </li>
                </ul>

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={() => dispatch(closeAdminModal())}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="lg"
                    onClick={() => {
                      dispatch(
                        InactiveUserSessionsAction({
                          id: row?.id,
                          filter: { id: userId, ...filterObj },
                        })
                      );
                      dispatch(closeAdminModal());
                    }}
                  >
                    Inactivate session
                  </Button>
                </div>
              </div>
            ),
          })
        );
      },
    },
    {
      label: "Delete",
      icon: <Trash size={16} color="#475569" />,
      onClick: () => {
        dispatch(
          openAdminModal({
            title: "",
            content: (
              <div className="grid gap-5 p-1">
                {/* Title with icon */}
                <div className="flex items-center gap-3.5">
                  <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                    <Trash className="size-5" aria-hidden="true" />
                  </div>
                  <h2 className="text-xl font-bold text-foreground">
                    Delete this session?
                  </h2>
                </div>

                {/* What happens */}
                <ul className="grid gap-2">
                  <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
                    <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
                    <span className="break-words">
                      This session will be permanently removed from the system.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
                    <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
                    <span>This action cannot be undone.</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
                    <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
                    <span>The user will be logged out of this device if currently active.</span>
                  </li>
                </ul>

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={() => dispatch(closeAdminModal())}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="lg"
                    onClick={() => {
                      dispatch(
                        DeleteUserSessionsAction({
                          id: row?.id,
                          filter: { id: userId, ...filterObj },
                        })
                      );
                      dispatch(closeAdminModal());
                    }}
                  >
                    Delete session
                  </Button>
                </div>
              </div>
            ),
          })
        );
      },
    },
  ].filter(Boolean);

  // For action menu
  const [actionMenu, setActionMenu] = useState(null);
  const handleActionMenuOpen = (event) => {
    setActionMenu(event.currentTarget);
  };
  const handleActionMenuClose = () => {
    setActionMenu(null);
  };

  return (
    <MuiBox className="flex h-full items-center justify-center gap-3">
      <Button
        className="size-7"
        mode="icon"
        variant="ghost"
        onClick={handleActionMenuOpen}
      >
        <EllipsisVertical />
      </Button>
      <Menu
        anchorEl={actionMenu}
        open={Boolean(actionMenu)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          " & .MuiMenu-paper": {
            mt: "10px",
            background: "#fff",
            boxShadow:
              "0 4px 6px -1px #e2e8f0, 0 2px 4px -2px #e2e8f0 !important",
          },
        }}
      >
        {ActionMenuArray.map((option) => (
          <MenuItem
            key={option.label}
            onClick={() => {
              handleActionMenuClose();
              option.onClick();
            }}
          >
            <MuiBox sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
              <MuiBox sx={{ width: "fit-content" }}>{option.icon}</MuiBox>
              <MuiTypography
                sx={{ fontWeight: "500", fontSize: "13px", color: "#475569" }}
              >
                {option.label}
              </MuiTypography>
            </MuiBox>
          </MenuItem>
        ))}
      </Menu>
    </MuiBox>
  );
};

export default UserSessionActionBar;